import { UrlRedirectComponent } from './../pages/url-redirect/url-redirect.component';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { EnvironmentService } from '../services/enviroment.service';
import { environment } from '../services/global-environment';
import { MfaCheckResponse } from '../models/mfa.model';
import { I18nService } from './i18n.service';

import { Router } from '@angular/router';
import { MfaService } from './mfa.service';

// 定義 API 回應介面
interface UserPermissionApiResponse {
  success: boolean;
  data: string[];
  message?: string;
}

// 定義使用者角色 API 回應介面
interface UserRoleApiResponse {
  success: boolean;
  data: string[];
  message?: string;
}

// 定義角色選項介面
export interface RoleOption {
  label: string;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private apiUrl = environment.apiUrl; // API 端點

  constructor(
    private http: HttpClient,
    private router: Router,
    private mfaService: MfaService,
    private i18nService: I18nService,
    private environmentService: EnvironmentService
  ) {}

  // 角色選項設定（支援多國語系）
  get roleOptions(): RoleOption[] {
    return [
      { label: this.i18nService.translate('role.super_admin'), value: 'super_admin' },
      { label: this.i18nService.translate('role.admin'), value: 'admin' },
      { label: this.i18nService.translate('role.user'), value: 'user' }
    ];
  }

  // 檢查使用者是否已登入
  isLoggedIn(): boolean {
    // 🔹 檢查 JWT Access Token（JWT 導入後使用）
    const accessToken = localStorage.getItem('accessToken');
    // 🔹 向下相容：如果沒有 accessToken，檢查舊的 authToken
    const authToken = localStorage.getItem('authToken');
    return !!(accessToken || authToken);
  }

  // 取得使用者資訊
  getUserInfo() {
    return {
      username: localStorage.getItem('username') || '',
      companyid: localStorage.getItem('companyid') || '',
      modules: JSON.parse(localStorage.getItem('modules') || '[]'),
      userid: localStorage.getItem('userid') || '',
      hasApprovalPermission: localStorage.getItem('hasApprovalPermission') === 'true',
    };
  }

  //取得使用者針對特定模組的角色權限
  getUserRolesForModule(assistantNo: string): string[] {
    // 🧪 檢查測試模式（僅在開發環境啟用）
    // 從 EnvironmentService 取得 production 設定
    const isProduction = this.environmentService.isProduction;
    const isDevelopment = !isProduction;

    if (isDevelopment) {
      const urlParams = new URLSearchParams(window.location.search);
      const testMode = urlParams.get('testmode') === 'true';
      const testRoleId = urlParams.get('roleid');

      if (testMode && testRoleId) {
        return [testRoleId];
      }
    }

    const userInfo = this.getUserInfo();
    const { modules } = userInfo;

    // 優先檢查是否有 ALL 權限
    const allModule = modules.find((m: any) => m.assistantNo === 'ALL');
    if (allModule && allModule.permission) {
      return [allModule.permission]; // 回傳 ALL 權限的角色
    }

    // 如果沒有 ALL 權限，檢查指定模組的權限
    const module = modules.find((m: any) => m.assistantNo === assistantNo);
    if (module) {
      // 檢查是否有 roles 欄位（新格式）或 permission 欄位（舊格式）
      if (module.roles) {
        return module.roles; // 回傳角色清單
      } else if (module.permission) {
        return [module.permission]; // 回傳單一權限作為陣列
      }
    }
    return []; // 若模組不存在或無角色，回傳空陣列
  }


  // 載入使用者權限 - 取得使用者可瀏覽的部門(如果是超級管理員，則傳回全部)
  load_user_permission_Depaerments(assistantNo:string): Promise<string[]> {
    return new Promise((resolve, reject) => {
      // 取得當前使用者資訊
      const userInfo = this.getUserInfo();
      const { companyid, userid,modules } = userInfo;

      // 檢查必要參數
      if (!companyid || !userid) {
        console.error('無法取得使用者權限：缺少 companyid 或 userid');
        resolve([]); // 回傳空陣列而非拋出錯誤，避免影響後續流程
        return;
      }

      // 檢查是否為超級管理員
      const userRoles = this.getUserRolesForModule(assistantNo);
      var isSuperAdmin = userRoles.includes('super_admin');

      var url = "";
      if (!isSuperAdmin) {
        // 建立 API URL - 一般使用者取得可存取的部門
        url = `${this.apiUrl}/api/departments/user-departments/${assistantNo}/${userid}`;
      } else {
        // 如果是超級管理員，呼叫取得所有部門的 API
        url = `${this.apiUrl}/api/departments/all-departments/${assistantNo}`;
      }


      console.log(`載入使用者權限，URL: ${url}`);

      // 呼叫 API 取得使用者可存取的部門ID清單
      this.http.get<any>(url).subscribe({
        next: (response) => {
          if (response.success && response.data && Array.isArray(response.data)) {
            let departmentIds: string[] = [];

            if (isSuperAdmin) {
              // 超級管理員：API 回傳部門物件陣列 [{ id: string, name: string }]
              departmentIds = response.data;
              console.log('成功取得超級管理員所有部門清單:', departmentIds);
            } else {
              // 一般使用者：API 回傳部門ID字串陣列
              departmentIds = response.data;
              console.log('成功取得使用者權限部門清單:', departmentIds);
            }

            resolve(departmentIds);
          } else {
            console.warn('API 回應成功但無有效資料:', response);
            resolve([]); // 回傳空陣列
          }
        },
        error: (error) => {
          console.error('取得使用者權限時發生錯誤:', error);
          // 不拋出錯誤，而是回傳空陣列，避免影響其他功能
          resolve([]);
        }
      });
    });
  }

  // 取得使用者角色ID清單(這個應該是用不到)
  // getUserRoleIdsInDepartment(companyid: string, assistantNo: string): Promise<string[]> {
  //   return new Promise((resolve) => {
  //     // 取得當前使用者資訊
  //     const userInfo = this.getUserInfo();
  //     const { userid } = userInfo;

  //     if (!userid) {
  //       console.error('無法取得使用者ID');
  //       resolve([]); // 即使沒有 userid 也要 resolve，避免阻塞後續流程
  //       return;
  //     }

  //     // 建立 API URL
  //     const url = `${this.apiUrl}/api/departments/user-roles/${assistantNo}/${userid}`;

  //     console.log(`載入使用者角色ID，URL: ${url}`);

  //     // 呼叫 API 取得使用者角色ID
  //     this.http.get<UserRoleApiResponse>(url).subscribe({
  //       next: (response) => {
  //         if (response.success && response.data) {
  //           console.log('成功取得使用者角色ID清單:', response.data);
  //           resolve(response.data);
  //         } else {
  //           console.warn('API 回應成功但無資料或失敗:', response);
  //           resolve([]);
  //         }
  //       },
  //       error: (error) => {
  //         console.error('取得使用者角色ID失敗:', error);
  //         // 發生錯誤時使用空陣列，避免影響後續功能
  //         resolve([]);
  //       }
  //     });
  //   });
  // }

  // 檢查是否有指定模組的權限
  hasModuleAccess(moduleId: string): boolean {
    if (!this.isLoggedIn()) return false;

    const modulesStr = localStorage.getItem('modules');
    if (!modulesStr) return false;

    try {
      const modules = JSON.parse(modulesStr);

      // 檢查格式，若是新格式(物件陣列)
      if (modules.length > 0 && typeof modules[0] === 'object') {
        // 檢查是否有 ALL 權限或指定模組權限
        return modules.some((m: any) =>
          m.assistantNo === 'ALL' || m.assistantNo === moduleId
        );
      }
      // 舊格式(字串陣列)
      else {
        return modules.includes('ALL') || modules.includes(moduleId);
      }
    } catch (e) {
      console.error('Error parsing modules from localStorage:', e);
      return false;
    }
  }

  // 重新導向到登入頁
  redirectToLogin() {
    this.router.navigate(['/login']);
  }

  login(credentials: { username: string; password: string }): Observable<any> {

    const test_account = environment.test_account;

    if (
      credentials.username === test_account &&  credentials.password === test_account
    ) {
      // 模擬成功的回應
      return of({ success: true, token: 'fake-jwt-token' });
    } else {
      // 模擬失敗的回應
      return throwError(() => new Error('Invalid username or password'));
    }
  }

  getJwtToken(username: string): Observable<{ accessToken: string }> {
    return this.http.post<{ accessToken: string }>(
      `${this.apiUrl}/api/auth/token`,
      { username },
      {
        headers: new HttpHeaders({ 'Content-Type': 'application/json' })
      }
    );
  }

  // 🔹 儲存 Token 到 localStorage
  storeToken(
    token: string,
    companyid: string,
    username: string,
    modules: string[],
    id: string,
    hasApprovalPermission?: boolean,
    accessToken?: string,
    refreshToken?: string
  ): void {
    // 🔍 Debug: Log what tokens are being stored
    console.log('🔍 storeToken called with:', {
      hasAuthToken: !!token,
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      username,
      userid: id
    });

    localStorage.setItem('authToken', token);
    localStorage.setItem('companyid', companyid);
    localStorage.setItem('username', username);
    localStorage.setItem('modules', JSON.stringify(modules));
    localStorage.setItem('userid', id);
    localStorage.setItem('hasApprovalPermission', hasApprovalPermission ? 'true' : 'false');

    // 儲存真實 JWT Tokens（如果有提供）
    if (accessToken) {
      localStorage.setItem('accessToken', accessToken);
      console.log('✅ Access Token stored in AuthService');
    } else {
      console.warn('⚠️ No accessToken provided to storeToken!');
    }
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
      console.log('✅ Refresh Token stored in AuthService');
    } else {
      console.warn('⚠️ No refreshToken provided to storeToken!');
    }
  }

  // 🔹 清除 Token (登出)
  clearToken() {
    localStorage.clear();
    // Reset MFA state on logout
    this.mfaService.resetSetupState();
    this.mfaService.updateMfaStatus({ isEnabled: false, hasBackupCodes: false });
  }

  // 🔹 Check if user requires MFA verification (called AFTER verify_password - JWT available)
  checkMfaRequired(username: string, companyid: string, accessToken?: string): Observable<MfaCheckResponse> {
    // 使用 JWT Token 認證，從 token 取得正確的 username 和 companyid
    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json'
    };

    // 如果有提供 accessToken，加入 Authorization header
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    return this.http.post<MfaCheckResponse>(
      `${this.apiUrl}/api/auth/mfa/check-mfa`,
      {}, // Body 不需要傳送 username/companyid，從 JWT 取得
      {
        headers: new HttpHeaders(headers)
      }
    ).pipe(
      catchError(() => of({
        success: false,
        data: { mfaRequired: false, mfaSetupRequired: false },
        message: 'Error checking MFA requirement'
      })) // Default to no MFA required on error
    );
  }

  // 🔹 Update user MFA status
  loadUserMfaStatus(username: string): void {
    // This would load the user's MFA status from the backend
    // For now, we'll check localStorage or make an API call
    this.http.post<{ isEnabled: boolean, hasBackupCodes: boolean, setupDate?: string }>(
      `${this.apiUrl}/api/auth/mfa/status`,
      { username },
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
        })
      }
    ).subscribe({
      next: (status) => {
        this.mfaService.updateMfaStatus(status);
      },
      error: (error) => {
        console.error('Failed to load MFA status:', error);
        // Default to MFA disabled if we can't load status
        this.mfaService.updateMfaStatus({ isEnabled: false, hasBackupCodes: false });
      }
    });
  }

}
